import { LineChart } from "echarts/charts";
import {
  GridComponent,
  TooltipComponent,
  TitleComponent,
  DatasetComponent,
  LegendComponent,
  DataZoomComponent,
  AriaComponent,
  TimelineComponent,
} from "echarts/components";
import { SVGRenderer } from "echarts/renderers";
import { UniversalTransition, LabelLayout } from "echarts/features";
import * as echarts from "echarts/core";
import ReactEChartsCore from "echarts-for-react/lib/core";
import React, { useEffect, useRef, useState } from "react";
import { showToast } from "components/Toast/toast.slice";
import store from "app/store";
import { getQaAutomationReport } from "services/api/GetQaAutomationReportsApi";

// Forecast Automation Graph Data
interface ForecastAutomationGraphData {
  xAxis: string[];
  yAxisTargeted: any[];
  yAxisCompleted: any[];
}

interface ForecastAutomationGraphValue {
  week: string[];
  totalAutomated: number[];
  totalPending: number[];
  totalCurrentCompleted: number[];
}

const LineForcastGraph: any = (props: any) => {
  const { projectName, startDate, endDate, graphType } = props;
  const predictionType = "Immediate";
  // Automation Graph related JS
  const [forecastAutomationGraphData, setForecastAutomationGraphData] =
    useState<ForecastAutomationGraphData>({
      xAxis: [
        "week1",
        "week2",
        "week3",
        "week4",
        "week5",
        "week6",
        "week7",
        "week8",
        "week9",
        "week10",
        "week11",
        "week12",
        "week13",
        "week14",
        "week15",
        "week16",
        "week17",
        "week18",
        "week19",
        "week20",
      ],
      yAxisTargeted: [],
      yAxisCompleted: [0, 20, 30, 50, 52, 60, 70, 80, 90, 100],
    });
  const setOptionsForecast = () => {
    return {
      baseOption: {
        aria: {
          enabled: true,
          label: {
            enabled: true,
          },
        },
        tooltip: {
          trigger: "item",
          position: "top",
          backgroundColor: "#000000",
          textStyle: {
            color: "#FFFFFF",
            fontSize: 10,
          },
          axisPointer: {
            type: "cross",
          },
          extraCssText: "z-index: 1991 !important;",
        },
        legend: {
          orient: "horizontal",
          x: "center",
          y: "bottom",
          icon: "circle",
          textStyle: {
            fontFamily: "Montserrat",
            fontWeight: 500,
            color: "#222328",
            fontSize: 14,
          },
          itemGap: 20,
          lineHeight: 18,
          itemHeight: 9,
          itemWidth: 9,
          padding: 10,
        },
        grid: {
          top: "5%",
          left: "0%",
          right: "9%",
          bottom: "20%",
          containLabel: true,
        },
        xAxis: {
          data: forecastAutomationGraphData.xAxis,
          type: "category",
          splitLine: {
            lineStyle: {
              width: 1,
              opacity: 0.5,
              color: "rgba(223, 223, 230, 1)",
            },
            show: true,
          },
          nameTextStyle: {
            fontFamily: " Montserrat",
            fontSize: 18,
            fontWeight: 600,
            lineHeight: 27,
            color: "#222328",
            align: "center",
            verticalAlign: "middle",
            overflow: "truncate",
            ellipsis: "...",
          },
          axisLabel: {
            formatter: (value: any) => {
              const newDates: any = `${value}`;
              return newDates;
            },
          },
          boundaryGap: false,
          axisLine: {
            show: false,
          },
          axisTick: {
            show: false,
          },
        },
        yAxis: {
          type: "value",
          axisLabel: {
            formatter(a: any) {
              a = +a;
              return Number.isFinite(a) ? echarts.format.addCommas(+a * 1) : "";
            },
          },
        },
        dataZoom: [
          {
            type: "inside",
            start: 0,
            end: 100,
          },
        ],
        textStyle: {
          fontFamily: " Montserrat",
          fontSize: 14,
          fontWeight: 600,
          lineHeight: 17,
          color: "#222328",
          align: "center",
          verticalAlign: "middle",
        },
        series: [
          {
            name: "Completed",
            type: "line",
            smooth: true,
            data: forecastAutomationGraphData.yAxisCompleted,
            itemStyle: {
              color: "#707070",
            },
            emphasis: {
              focus: "self",
              // blurScope: "coordinateSystem",
            },
            lineStyle: {
              width: 2,
              type: "solid",
              cap: "round",
            },
          },
          {
            name: "Targeted",
            type: "line",
            smooth: true,
            data: forecastAutomationGraphData.yAxisTargeted, // You have to add logic yourself eg: Data should be of the form [0, 0, 0, 0, 0, 0, 0, 0, 0, ...TargetedDats]
            emphasis: {
              focus: "self",
              // blurScope: "coordinateSystem",
            },
            itemStyle: {
              color: "#FCC503",
            },
            lineStyle: {
              width: 2,
              type: "dashed",
              cap: "round",
            },
          },
        ],
      },
    };
  };

  // COMMON CODE
  const [selectedWeeks, setSelectedWeeks] = useState<number>(4);
  const [loading, setLoading] = useState(true);
  const getCurrentEchartInstance = (chart: any) => {
    return chart.getEchartsInstance();
  };

  // Track and update chart options
  const chartDom1 = useRef(null);
  let chartInstance1: any = null;

  async function renderChart1(chart: any) {
    const renderInstance = getCurrentEchartInstance(chart);
    if (renderInstance) {
      chartInstance1 = renderInstance;
    } else {
      chartInstance1 = echarts.init(chart);
    }
    if (
      forecastAutomationGraphData.yAxisCompleted.reduce(
        (a: number, b: number) => a + b,
        0
      ) === 0 &&
      forecastAutomationGraphData.yAxisTargeted.reduce(
        (a: any, b: any) => a + b,
        0
      ) === 0
    ) {
      renderInstance.setOption({
        title: {
          show: true,
          textStyle: {
            color: "grey",
            fontSize: 20,
          },
          text: "No data",
          left: "center",
          top: "center",
        },
      });
    } else {
      await chartInstance1.setOption({
        baseOption: {
          title: {
            show: false,
          },
          xAxis: {
            data: forecastAutomationGraphData.xAxis,
          },
          series: [
            {
              name: "Completed",
              data: forecastAutomationGraphData.yAxisCompleted,
            },
            {
              name: "Targeted",
              data: forecastAutomationGraphData.yAxisTargeted,
            },
          ],
        },
        // All EChart Supported Media Queries are relative to the size of the charts and not the current window size
        media: [
          {
            option: {
              series: [
                {
                  name: "Completed",
                  emphasis: {
                    disabled: false,
                    focus: "self",
                  },
                },
                {
                  name: "Targeted",
                  emphasis: {
                    disabled: false,
                    focus: "self",
                  },
                },
              ],
            },
          },
          {
            query: {
              maxWidth: 767,
            },
            option: {
              series: [
                {
                  name: "Completed",
                  emphasis: {
                    disabled: true,
                  },
                },
                {
                  name: "Targeted",
                  emphasis: {
                    disabled: true,
                  },
                },
              ],
            },
          },
        ],
      });

      await chartInstance1.setOption({
        xAxis: {
          axisLabel: {
            formatter: (value: any) => {
              const newDates: any = new Date(`${value}`).toLocaleDateString(
                "en-US",
                {
                  day: "2-digit",
                  month: "short",
                }
              );
              return newDates;
            },
          },
        },
      });
    }
    setLoading(false);
  }
  // Immediate Timeline Prediction
  const prediction = (currentData: any, number: any) => {
    let avg = 0;
    let counter = 0;
    number += currentData.length;
    // Calculate average of last 4 weeks data
    for (let i = currentData.length - 1; i >= 0 && counter < 4; i -= 1) {
      counter += 1;
      avg += currentData[i];
    }
    avg /= counter; // Calculate average
    const predictData = [];
    let itr = currentData[currentData.length - 1];
    // Predict next weeks' data
    for (let i = 0; i < number - currentData.length; i += 1) {
      itr += avg;
      predictData.push(Math.round(itr));
    }
    // making current week data elements null
    const currentData1 = currentData.map(() => null);
    const data = [...currentData1, ...predictData];
    for (let key = 0; key < currentData.length; key += 1) {
      if (key === currentData.length - 1) {
        data[key] = currentData[key];
      }
    }
    return data;
  };

  // Call API to Update Chart
  const GetQaAutomationForcastReportsApi = async () => {
    getQaAutomationReport({ projectName, startDate, endDate })
      .then(async (response: any) => {
        const { qaReports } = response.data;
        let xaxis: any[] = [];
        let yaxiscompleted: any[] = [];
        let yaxissqstargeted: any[] = [];
        qaReports.forEach((val: ForecastAutomationGraphValue) => {
          // const newDates: any = new Date(`${val.week}`); // Change the date format as per your requirement
          yaxiscompleted = [...yaxiscompleted, val.totalAutomated];
        });
        for (let i = 0; i < yaxiscompleted.length + 4; i += 1) {
          xaxis = [...xaxis, `Week ${i + 1}`];
        }
        if (predictionType === "Immediate") {
          yaxissqstargeted = [
            ...yaxissqstargeted,
            prediction(yaxiscompleted, 4),
          ];
        }
        setForecastAutomationGraphData({
          ...forecastAutomationGraphData,
          xAxis: xaxis,
          yAxisCompleted: yaxiscompleted,
          yAxisTargeted: yaxissqstargeted[0],
        });
        setLoading(false);
      })
      .catch((error: any) => {
        setLoading(false);
        store.dispatch(
          showToast({
            type: "danger",
            title: "Error Occurred",
            message: error.message,
          })
        );
      });
  };
  // Use Effect to monitor and update the graph on change in dropdown
  useEffect(() => {
    // Axios request to hit endpoint
    setLoading(true);
    GetQaAutomationForcastReportsApi();

    // Remove after API integration
    // Immediate Timeline Prediction
    if (predictionType === "Immediate") {
      setForecastAutomationGraphData({
        ...forecastAutomationGraphData,
        yAxisTargeted: prediction(
          forecastAutomationGraphData.yAxisCompleted,
          4
        ),
      });
    }
  }, [startDate, endDate, projectName, graphType]);

  useEffect(() => {
    // Update chart values

    if (chartDom1.current !== null) {
      const lineChart = chartDom1.current;
      renderChart1(lineChart);
    }
    document
      .querySelector<any>(".echarts-for-react")
      ?.setAttribute("role", "img");
  }, [forecastAutomationGraphData]);

  useEffect(() => {
    return () => {
      if (chartDom1.current !== null) {
        chartInstance1 = getCurrentEchartInstance(chartDom1.current);
      }
    };
  }, []);

  // Loading elements
  const loadingOption = {
    text: "loading",
    color: "#c23531",
    textColor: "#000",
    maskColor: "rgba(255, 255, 255, 0.8)",
    zlevel: 0,
    fontSize: 12,
    showSpinner: true,
    spinnerRadius: 10,
    lineWidth: 5,
    fontWeight: "normal",
    fontStyle: "normal",
  };
  useEffect(() => {
    // Update chart
    if (chartDom1.current !== null) {
      const chart = getCurrentEchartInstance(chartDom1.current);
    }
  }, [loading]);

  // Register the required components
  echarts.use([
    // CanvasRenderer,
    SVGRenderer,
    GridComponent,
    TooltipComponent,
    TitleComponent,
    DatasetComponent,
    LegendComponent,
    LineChart,
    UniversalTransition,
    DataZoomComponent,
    LabelLayout,
    AriaComponent,
    TimelineComponent,
  ]);
  return (
    <div>
      <ReactEChartsCore
        echarts={echarts}
        ref={chartDom1}
        option={setOptionsForecast()}
        className="echart-wrapper transaction-log px-3 mx-2"
        loadingOption={loadingOption}
        showLoading={loading}
        opts={{ renderer: "svg" }}
        lazyUpdate
      />
    </div>
  );
};

export default LineForcastGraph;
